import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { pluralize } from '@gonfalon/format/pluralize';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toMetricGroup } from '@gonfalon/navigator/navigators';
import { useMetricGroup } from '@gonfalon/rest-api/hooks/useMetricGroup';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { TextTruncatorWithTooltip } from '@gonfalon/ui/TextTruncatorWithTooltip';
import { LinkButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { metricsSearchTracker } from '../../analytics';
import { MetricGroupTypeLabel } from '../../MetricLabel';
import type { MetricGroupRep, MetricInGroupRep } from '../../types';
import { getMetricGroupExpandParams } from '../MetricGroups/getMetricGroupExpandParams';

import { trackMetricSelectionLinkToFullResourceClicked } from './analytics';

import styles from './MetricGroupDetailsPanel.module.css';

interface MetricGroupDetailsPanelProps {
  metricGroup: MetricGroupRep;
  unavailableReason?: string;
}

function getConnectionCount(item: MetricGroupRep) {
  return (item?.experimentCount ?? 0) + (item?.guardedRolloutCount ?? 0) + (item?.releasePolicyCount ?? 0);
}

function MetricGroupDetailsPanelInner({ metricGroup, unavailableReason }: MetricGroupDetailsPanelProps) {
  const projectKey = useProjectKey();
  const path = toMetricGroup({ projectKey, metricGroupKey: metricGroup.key });

  const { data: fullMetricGroup } = useMetricGroup({
    projectKey,
    metricGroupKey: metricGroup.key,
    params: { expand: getMetricGroupExpandParams() },
  });

  const releasePolicies = fullMetricGroup?.releasePolicies;

  return (
    <div className={styles.detailsPanel}>
      {/* Name */}
      <div className={styles.section}>
        <TextTruncatorWithTooltip tooltipContent={metricGroup.name} className={styles.metricName} maxLines={2}>
          {metricGroup.name}
        </TextTruncatorWithTooltip>

        <div className={styles.metricGroupData}>
          <span>{metricGroup.metrics.length ?? 0} metrics</span>
          <span>
            {getConnectionCount(metricGroup)}{' '}
            {pluralize(getConnectionCount(metricGroup) ?? 0, 'connection', 'connections')}
          </span>
        </div>

        <MetricGroupTypeLabel id={metricGroup.kind} />
      </div>

      {unavailableReason && (
        <div className={styles.unavailableReason}>
          <div className={styles.unavailableReasonTitle}>
            <Icon size="tiny" name="info-circle-outline" className={styles.unavailableReasonIcon} />{' '}
            <Message id="metrics.searchDetailsPanel.unavailableForSelection" />
          </div>
          {unavailableReason}
        </div>
      )}
      {/* Description */}
      {metricGroup.description && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>
            <Message id="metrics.searchDetailsPanel.description" />
          </h4>
          <p className={styles.sectionContent}>{metricGroup.description}</p>
        </div>
      )}

      {metricGroup.metrics.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>
            <Message id="metrics.searchDetailsPanel.metricsInGroup" />
          </h4>
          <div className={styles.sectionContent}>
            {metricGroup.metrics.map((metric: MetricInGroupRep) => (
              <span className={styles.metricInGroupItem} key={metric.key}>
                <Icon name="ruler" size="small" />
                <TextTruncatorWithTooltip tooltipContent={metric.name}>{metric.name}</TextTruncatorWithTooltip>
              </span>
            ))}
          </div>
        </div>
      )}

      {releasePolicies && releasePolicies.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>
            <Message id="metrics.searchDetailsPanel.releasePolicies" />
          </h4>
          <div className={clsx(styles.sectionContent, styles.releasePolicyContent)}>
            {releasePolicies.map((policy) => (
              <span key={policy.key} className={styles.releasePolicyItem}>
                <TextTruncatorWithTooltip tooltipContent={policy.name}>{policy.name}</TextTruncatorWithTooltip>
              </span>
            ))}
          </div>
        </div>
      )}

      <LinkButton
        href={path}
        target="_blank"
        className={styles.linkButton}
        aria-label={msg('metrics.searchDetailsPanel.viewGroupLabel')}
        onPress={() => {
          const attributes = { kind: 'metricGroup' } as const;
          trackMetricSelectionLinkToFullResourceClicked(attributes);
          metricsSearchTracker.track('Link to Full Resource Clicked', attributes);
        }}
      >
        <Icon name="link-external" size="small" />
      </LinkButton>
    </div>
  );
}

export function MetricGroupDetailsPanel(props: MetricGroupDetailsPanelProps) {
  return (
    <ErrorBoundary
      owner="team-agent-platform"
      severity="medium"
      fallbackRender={() => (
        <div className={styles.errorContainer} style={{ padding: 'var(--lp-spacing-400)' }}>
          <span className={styles.errorMessage} style={{ color: 'var(--lp-color-text-ui-primary-base)' }}>
            <Message id="metrics.searchDetailsPanel.groupLoadError" />
          </span>
        </div>
      )}
    >
      <MetricGroupDetailsPanelInner {...props} />
    </ErrorBoundary>
  );
}
